/**
 * Shared status badge colors for StatusDropdown, map pins, and dashboard legend dots.
 * Keep in sync with product palette — backgroundColor is the pin / legend fill.
 */

import { getContrastTextColor } from '../utils/colorUtils';

/** Product status fills with black/white label contrast. */
const badge = (backgroundColor) => ({
  backgroundColor,
  color: getContrastTextColor(backgroundColor),
});

export const STATUS_PALETTE = {
  activeEmployee: { backgroundColor: '#86EFAC', color: '#14532D' },
  clockIn: badge('#22C55E'),
  clockOut: badge('#3B82F6'),
  breakStart: badge('#F59E0B'),
  breakEnd: badge('#06B6D4'),
  lateClockIn: badge('#EF4444'),
  lateClockOut: badge('#EA580C'),
  /** Legacy revoke styling — not part of attendance clock-out palette */
  revokedStatus: { backgroundColor: '#991B1B', color: '#FECACA' },
  earlyClockIn: badge('#6366F1'),
  earlyClockOut: badge('#EAB308'),
  onTime: badge('#10B981'),
  employeesAbsent: { backgroundColor: '#171717', color: '#D4D4D4' },
  unvalidatedPunches: badge('#6B7280'),
  /** Legacy error/expired chip styling (unchanged when unvalidated gray was updated) */
  expiredStatus: { backgroundColor: '#D1D5DB', color: '#374151' },
  invalidPunch: badge('#DC2626'),
  /** Attendance history HAS_ISSUES — purple; strong text for chips + map pins */
  attendanceHasIssues: { backgroundColor: '#DDD6FE', color: '#4C1D95' },
  /** Attendance history late / early-out / late+early — slate gray, visible on maps */
  attendanceTimingGray: { backgroundColor: '#CBD5E1', color: '#0F172A' },
  /** Inactive users — light red (not gray/white) for badges, legend dots, and map pin fill */
  inactiveUser: { backgroundColor: '#FECACA', color: '#991B1B' },
  validatedPunches: badge('#16A34A'),
  dayLeave: { backgroundColor: '#DDD6FE', color: '#5B21B6' },
  halfDayLeave: badge('#8B5CF6'),
  pendingTask: { backgroundColor: '#F97316', color: '#7C2D12' },
  pendingRequests: badge('#F59E0B'),
  underReview: badge('#3B82F6'),
  pendingReimbursement: { backgroundColor: '#FB923C', color: '#431407' },
  pendingDocuments: { backgroundColor: '#FDBA74', color: '#7C2D12' },
  pendingRegistrations: { backgroundColor: ' #F5CD47', color: '#533F04' },

  /** Task workflow — assigned (blue), in progress (orange), completed (green) */
  taskAssigned: { backgroundColor: '#579DFF', color: '#09326C' },
  taskInProgress: { backgroundColor: '#F97316', color: '#7C2D12' },
  taskCompleted: { backgroundColor: '#22C55E', color: '#14532D' },
  /** Task priority — distinct fills only; layout unchanged in StatusDropdown */
  taskPriorityUrgent: { backgroundColor: '#DC2626', color: '#FEF2F2' },
  taskPriorityHigh: { backgroundColor: '#F97316', color: '#7C2D12' },
  taskPriorityMedium: { backgroundColor: '#EAB308', color: '#713F12' },
  taskPriorityLow: { backgroundColor: '#D1D5DB', color: '#374151' },
};

export const statusStyles = {
  CLOCK_IN: STATUS_PALETTE.clockIn,
  ON_TIME_CLOCK_IN: STATUS_PALETTE.onTime,
  ON_TIME_CLOCK_OUT: STATUS_PALETTE.onTime,
  /** Today’s attendance — late arrival (alias of LATE_ARRIVAL for API/filter keys) */
  LATE_CLOCK_IN: STATUS_PALETTE.lateClockIn,
  Present: STATUS_PALETTE.clockIn,
  PRESENT: STATUS_PALETTE.clockIn,
  sent: STATUS_PALETTE.clockIn,

  Validated: STATUS_PALETTE.validatedPunches,
  completed: STATUS_PALETTE.taskCompleted,
  Verified: STATUS_PALETTE.validatedPunches,
  verified: STATUS_PALETTE.validatedPunches,
  Approved: STATUS_PALETTE.clockIn,
  APPROVED: STATUS_PALETTE.clockIn,
  approved: STATUS_PALETTE.clockIn,
  Paid: STATUS_PALETTE.validatedPunches,
  paid: STATUS_PALETTE.validatedPunches,
  resolved: STATUS_PALETTE.validatedPunches,

  Active: STATUS_PALETTE.activeEmployee,
  active: STATUS_PALETTE.activeEmployee,

  escalated: STATUS_PALETTE.pendingTask,
  urgent: STATUS_PALETTE.taskPriorityUrgent,
  high: STATUS_PALETTE.taskPriorityHigh,

  Error: STATUS_PALETTE.expiredStatus,
  Invalid: STATUS_PALETTE.invalidPunch,
  Delayed: STATUS_PALETTE.lateClockIn,

  Rejected: STATUS_PALETTE.lateClockIn,
  REJECTED: STATUS_PALETTE.lateClockIn,
  rejected: STATUS_PALETTE.lateClockIn,

  UNDER_REVIEW: STATUS_PALETTE.underReview,
  'Under Review': STATUS_PALETTE.underReview,
  under_review: STATUS_PALETTE.underReview,
  underReview: STATUS_PALETTE.underReview,

  cancelled: STATUS_PALETTE.pendingReimbursement,
  CANCELLED: STATUS_PALETTE.pendingReimbursement,

  Absent: STATUS_PALETTE.employeesAbsent,
  ABSENT: STATUS_PALETTE.employeesAbsent,
  not_done: STATUS_PALETTE.employeesAbsent,

  CHECKED_OUT: STATUS_PALETTE.clockOut,
  CLOCK_OUT: STATUS_PALETTE.clockOut,

  Inactive: STATUS_PALETTE.inactiveUser,
  inactive: STATUS_PALETTE.inactiveUser,
  expired: STATUS_PALETTE.expiredStatus,

  revoked: STATUS_PALETTE.revokedStatus,

  Leave: STATUS_PALETTE.dayLeave,
  assigned: STATUS_PALETTE.taskAssigned,

  new: STATUS_PALETTE.pendingRegistrations,
  Invited: STATUS_PALETTE.pendingRegistrations,
  invited: STATUS_PALETTE.pendingRegistrations,
  email_pending: STATUS_PALETTE.pendingRegistrations,

  toBePaid: STATUS_PALETTE.pendingReimbursement,

  valid: STATUS_PALETTE.validatedPunches,

  /** Title-case — UnvalidatedPunches “Pending punch” */
  Pending: STATUS_PALETTE.pendingRequests,
  pending: STATUS_PALETTE.pendingRequests,
  PENDING: STATUS_PALETTE.pendingRequests,
  open: STATUS_PALETTE.pendingRequests,
  Requested: STATUS_PALETTE.pendingRequests,
  Request: STATUS_PALETTE.pendingRequests,

  INFO_REQUESTED: STATUS_PALETTE.pendingDocuments,
  REQUEST_INFO: STATUS_PALETTE.pendingDocuments,

  ON_LEAVE: STATUS_PALETTE.dayLeave,
  ON_HALF_LEAVE: STATUS_PALETTE.halfDayLeave,
  in_progress: STATUS_PALETTE.taskInProgress,
  'Half Leave': STATUS_PALETTE.halfDayLeave,
  Paused: STATUS_PALETTE.halfDayLeave,

  BACK_FROM_BREAK: STATUS_PALETTE.breakEnd,
  BREAK_END: STATUS_PALETTE.breakEnd,

  'Early Out': STATUS_PALETTE.earlyClockOut,
  EARLY_CLOCK_OUT: STATUS_PALETTE.earlyClockOut,
  Ongoing: STATUS_PALETTE.earlyClockOut,

  'Late Arrival': STATUS_PALETTE.attendanceTimingGray,
  LATE_ARRIVAL: STATUS_PALETTE.attendanceTimingGray,

  ON_BREAK: STATUS_PALETTE.breakStart,
  BREAK: STATUS_PALETTE.breakStart,
  BREAK_START: STATUS_PALETTE.breakStart,

  LATE_CLOCK_OUT: STATUS_PALETTE.lateClockOut,

  EARLY_CLOCK_IN: STATUS_PALETTE.earlyClockIn,

  low: STATUS_PALETTE.taskPriorityLow,
  medium: STATUS_PALETTE.taskPriorityMedium,

  /** Attendance history / employee filters */
  EARLY_OUT: STATUS_PALETTE.attendanceTimingGray,
  LATE_AND_EARLY_OUT: STATUS_PALETTE.attendanceTimingGray,
  'Late And Early Out': STATUS_PALETTE.attendanceTimingGray,
  HAS_ISSUES: STATUS_PALETTE.attendanceHasIssues,
  'Has Issues': STATUS_PALETTE.attendanceHasIssues,
  HALF_DAY: STATUS_PALETTE.halfDayLeave,
  'Half Day': STATUS_PALETTE.halfDayLeave,
  OVERTIME: STATUS_PALETTE.pendingTask,
  Overtime: STATUS_PALETTE.pendingTask,

  /** Task management (snake_case + API aliases) */
  delayed: STATUS_PALETTE.lateClockIn,
  overdue: STATUS_PALETTE.lateClockIn,
  unvalidated: STATUS_PALETTE.unvalidatedPunches,

  INVALID_FACE: STATUS_PALETTE.invalidPunch,
  INVALID_LOCATION: STATUS_PALETTE.invalidPunch,
  INVALID_SCHEDULE: STATUS_PALETTE.attendanceHasIssues,
  BOTH_ISSUES: STATUS_PALETTE.invalidPunch,
  VALID: STATUS_PALETTE.validatedPunches,
};

const FALLBACK_PIN_BG = '#4B9CD3';

const normalizeStatusLookupKey = (s) =>
  String(s || '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_')
    .toUpperCase();

const statusBackgroundByNormalizedKey = (() => {
  const m = {};
  Object.entries(statusStyles).forEach(([key, style]) => {
    const nk = normalizeStatusLookupKey(key);
    if (style?.backgroundColor) {
      m[nk] = style.backgroundColor;
    }
  });
  return m;
})();

const statusForegroundByNormalizedKey = (() => {
  const m = {};
  Object.entries(statusStyles).forEach(([key, style]) => {
    const nk = normalizeStatusLookupKey(key);
    if (style?.color) {
      m[nk] = style.color;
    }
  });
  return m;
})();

const FALLBACK_FG = '#374151';

/**
 * Resolve badge/pin background color for any API status string (same mapping as StatusDropdown).
 */
export function getStatusBadgeBackgroundColor(status) {
  if (status == null || status === '' || status === '-') return FALLBACK_PIN_BG;
  const raw = String(status).trim();
  const exact = statusStyles[raw];
  if (exact?.backgroundColor) return exact.backgroundColor;
  const nk = normalizeStatusLookupKey(raw);
  return statusBackgroundByNormalizedKey[nk] || FALLBACK_PIN_BG;
}

/** Foreground (text) color paired with `getStatusBadgeBackgroundColor` for the same status. */
export function getStatusBadgeForegroundColor(status) {
  if (status == null || status === '' || status === '-') return FALLBACK_FG;
  const raw = String(status).trim();
  const exact = statusStyles[raw];
  if (exact?.color) return exact.color;
  const nk = normalizeStatusLookupKey(raw);
  return statusForegroundByNormalizedKey[nk] || FALLBACK_FG;
}

/**
 * Google Maps Marker icon (SVG pin) using an arbitrary fill — same shape as dashboard pins.
 */
/** Pin ring + center dot; default white for contrast on most fills */
const PIN_ACCENT_DEFAULT = { stroke: '#ffffff', hole: '#ffffff' };
/** Inactive: replace white with red tints so the pin matches inactive styling */
const PIN_ACCENT_INACTIVE = { stroke: '#F87171', hole: '#FEE2E2' };
/** HAS_ISSUES — violet ring so light purple fill stays visible on the map */
const PIN_ACCENT_HAS_ISSUES = { stroke: '#6D28D9', hole: '#FAF5FF' };
/** Late / early timing — darker ring on slate fill */
const PIN_ACCENT_TIMING_GRAY = { stroke: '#334155', hole: '#F8FAFC' };

export function getGoogleMapsPinIconFromHex(pinFillHex, accent = PIN_ACCENT_DEFAULT) {
  const pinColor = pinFillHex || FALLBACK_PIN_BG;
  const stroke = accent?.stroke ?? PIN_ACCENT_DEFAULT.stroke;
  const hole = accent?.hole ?? PIN_ACCENT_DEFAULT.hole;
  const iconSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
      <path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.3 15 41 15 41S27.5 23.3 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill="${pinColor}" stroke="${stroke}" stroke-width="2"/>
      <circle cx="15" cy="13.5" r="4.5" fill="${hole}"/>
    </svg>
  `;
  const iconUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(iconSvg)}`;

  if (typeof window === 'undefined' || !window.google?.maps) {
    return { url: iconUrl };
  }

  return {
    url: iconUrl,
    scaledSize: new window.google.maps.Size(30, 42),
    anchor: new window.google.maps.Point(15, 41),
  };
}

/** Schedule timing keys used for per-punch map pins and drawer section indicators. */
const SCHEDULE_PIN_CLASSIFICATIONS = new Set([
  'EARLY_CLOCK_IN',
  'ON_TIME_CLOCK_IN',
  'LATE_CLOCK_IN',
  'EARLY_CLOCK_OUT',
  'ON_TIME_CLOCK_OUT',
  'LATE_CLOCK_OUT',
]);

/**
 * Status key for a punch pin / section dot — prefers schedule classification, then action type.
 * `fallbackStatus` is typically the row/day status when punch-level data is missing.
 */
export function resolvePunchPinStatusKey(punch, fallbackStatus = '') {
  const classification = String(punch?.schedule_timing?.classification ?? '')
    .trim()
    .toUpperCase();
  if (SCHEDULE_PIN_CLASSIFICATIONS.has(classification)) return classification;

  const timingCategory = String(
    punch?.timing?.attendanceCategory ?? punch?.attendanceCategory ?? ''
  )
    .trim()
    .toUpperCase();
  if (timingCategory) return timingCategory;

  const actionType = String(punch?.actionType ?? '').trim().toUpperCase();
  if (actionType === 'ON_BREAK') return 'BREAK_START';
  if (actionType) return actionType;

  return String(fallbackStatus ?? '').trim();
}

/** Pin fill matches StatusDropdown badge background for this status key. */
export function getGoogleMapsPinIconForStatus(rawStatus) {
  const bg = getStatusBadgeBackgroundColor(rawStatus);
  const nk = normalizeStatusLookupKey(rawStatus);
  if (nk === 'INACTIVE') {
    return getGoogleMapsPinIconFromHex(bg, PIN_ACCENT_INACTIVE);
  }
  if (nk === 'HAS_ISSUES') {
    return getGoogleMapsPinIconFromHex(bg, PIN_ACCENT_HAS_ISSUES);
  }
  if (
    nk === 'LATE_ARRIVAL' ||
    nk === 'LATE_AND_EARLY_OUT' ||
    nk === 'EARLY_OUT'
  ) {
    return getGoogleMapsPinIconFromHex(bg, PIN_ACCENT_TIMING_GRAY);
  }
  return getGoogleMapsPinIconFromHex(bg);
}



